import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useNotifyMe } from '~/hooks/useNotifyMe';
import { useProductLimit } from '~/hooks/useProductLimit';
import { cartStore, mainStore } from '~/stores';
import type { ProductState } from '~/stores/CategoriesStore';
import { isProductBundle } from '~/types/Product';
import { formatPriceWithCurrency } from '~/utils/formaters';
import {
  getApplicableMultibuyLevel,
  getLowestMultibuyLevel,
} from '~/utils/multibuy';

export const useProductNavigation = ({
  product,
}: {
  product: Nullable<ProductState>;
}) => {
  const [searchParams] = useSearchParams();
  const sku = searchParams.get('sku');

  const isBundle = product?.data ? isProductBundle(product.data) : false;

  const currentItem = isBundle
    ? product?.data
    : product?.selectedOffer ||
      product?.data?.offers.find((i) => i.sku === sku) ||
      product?.data;

  const count = currentItem ? cartStore.getCartItemCountById(currentItem) : 0;

  const lowestLevel = currentItem
    ? getLowestMultibuyLevel(currentItem.multibuyPriceLevels)
    : null;
  const applicableLevel = currentItem
    ? getApplicableMultibuyLevel(currentItem.multibuyPriceLevels, count)
    : null;
  const isMultibuy = !isBundle && Boolean(lowestLevel);
  const isMultibuyActivated = applicableLevel !== null;

  const { notifyMeOnClick, notifyMeIcon, notifyMeButtonText, btnClassName } =
    useNotifyMe(
      product?.data?.id || 0,
      product?.selectedOffer?.sku || '',
      isBundle,
    );

  const { limit } = useProductLimit({
    offer: currentItem,
  });

  // Keep the raw (unformatted) amount inside `useMemo`; currency formatting is
  // done at the top level because `formatPriceWithCurrency` reads global
  // context via a hook and must never be called from inside `useMemo`.
  const calculatedPriceRaw = useMemo(() => {
    if (isMultibuy && isMultibuyActivated && applicableLevel) {
      return mainStore.convertPenceToPounds(applicableLevel.unitPrice * count);
    }

    const priceToUse = currentItem?.discountPrice || currentItem?.price || 0;
    return mainStore.convertPenceToPounds(priceToUse * count);
  }, [isMultibuy, isMultibuyActivated, applicableLevel, currentItem, count]);

  const calculatedPrice = formatPriceWithCurrency(calculatedPriceRaw);

  return {
    product,
    currentItem,
    isBundle,
    count,
    limit,
    notifyMeOnClick,
    notifyMeIcon,
    notifyMeButtonText,
    btnClassName,
    calculatedPrice,
    isMultibuy,
    isMultibuyActivated,
  };
};
